import { ConfigurationProxy, KeyValueModel } from '@/app/proxy/proxies';
import { Injectable } from '@angular/core';
import { catchError, finalize, firstValueFrom, of, ReplaySubject, tap } from 'rxjs';
import { SessionStorageService } from '../storage/session-storage.service';

interface AppConfiguration {
    agreements: {
        tr: {
            accessKey: string;
            sales: string;
            terms: string;
        };
        en: {
            accessKey: string;
            sales: string;
            terms: string;
        };
    };
    googleAnalytics: {
        measurementId: string;
    };
    mixpanel: {
        projectToken: string;
        baseUrl: string;
    };
    sentinel: {
        ndviEnhancedPath: string;
        ndviPath: string;
        inspectionPath: string;
        ndmiSoilPngPath: string;
    };
    planet: {
        inspectionPath: string;
        ndviPath: string;
    };
}

class AppConfigKeys {
    agreementSalesEnKey = 'Core.Asset.Agreements';
    mixpanelProjectTokenKey = 'App.Backoffice.Mixpanel.ProjectToken';
    mixpanelBaseUrlKey = 'App.Mixpanel.BaseURL';
    googleAnalyticsMeasurementIdKey = 'Core.GoogleAnalytics.Backoffice.MeasurementId';
    cosmosDbExamplesUrlKey = 'App.Backoffice.Examples.CosmosDB';
    imagePathsKey = 'App.Backoffice.Image.Paths';
    signalrNegotiateUrlKey = 'Core.Notification.Negotiate.URL';

    getSatelliteKeys(): string[] {
        return [this.imagePathsKey];
    }
}

@Injectable({
    providedIn: 'root',
})
export class AppConfigService {
    private _appConfigKeys = new AppConfigKeys();
    private _appConfigurations: Record<string, string> | undefined;
    private _appConfigurationObject: AppConfiguration | undefined;
    private _appConfigurationsSubject = new ReplaySubject<Record<string, string>>(1);

    private readonly SESSION_STORAGE_KEY = 'app_configurations';
    private readonly SESSION_STORAGE_OBJECT_KEY = 'app_configuration_object';
    private _hasKeyValues = false;

    constructor(
        private _configService: ConfigurationProxy,
        private _sessionStorageService: SessionStorageService
    ) {}

    async initializeApp(): Promise<boolean> {
        if (this._hasKeyValues) {
            return true; // Already initialized, return immediately
        }

        // Try to load from session storage first
        const cachedConfig = this.loadFromSessionStorage();
        const cachedObject = this.loadObjectFromSessionStorage();
        if (cachedConfig && cachedObject) {
            this._appConfigurations = cachedConfig;
            this._appConfigurationObject = cachedObject;
            this._hasKeyValues = true;
            return true;
        }

        return new Promise<boolean>((resolve) => {
            if (!this._appConfigurationsSubject.observed) {
                this.performAppConfigurationsInitialization(this._appConfigKeys);
            }

            // Subscribe to the initialization subject to get notified when it's done
            this._appConfigurationsSubject.subscribe(() => {
                this._hasKeyValues = true;
                resolve(true);
            });
        });
    }

    async loadSatellitePaths(): Promise<boolean> {
        const satelliteKeys = this._appConfigKeys.getSatelliteKeys();

        try {
            const response = await firstValueFrom(
                this._configService.configuration_GetConfiguration(satelliteKeys).pipe(
                    catchError((error) => {
                        console.error('Satellite paths yüklenirken hata:', error);
                        return of(null);
                    })
                )
            );

            if (response && response.result) {
                const data = response.result as unknown as KeyValueModel[];

                const imagePathsItem = data[0];
                if (!imagePathsItem || !imagePathsItem.value) {
                    console.error('Image paths bulunamadı');
                    return false;
                }

                const imagePaths = JSON.parse(imagePathsItem.value) as Array<{ name: string; value: string }>;

                const pathsMap: Record<string, string> = {};
                imagePaths.forEach((path) => {
                    const containerName = path.value.split('/').pop() || path.value;
                    pathsMap[path.name] = containerName;
                });

                this._appConfigurations = { ...this._appConfigurations, ...pathsMap };

                if (!this._appConfigurationObject) {
                    this._appConfigurationObject = {
                        agreements: {
                            tr: { accessKey: '', sales: '', terms: '' },
                            en: { accessKey: '', sales: '', terms: '' },
                        },
                        mixpanel: {
                            projectToken: '',
                            baseUrl: '',
                        },
                        googleAnalytics: { measurementId: '' },
                        sentinel: {
                            ndviEnhancedPath: '',
                            ndviPath: '',
                            inspectionPath: '',
                            ndmiSoilPngPath: '',
                        },
                        planet: {
                            inspectionPath: '',
                            ndviPath: '',
                        },
                    };
                }

                this._appConfigurationObject.sentinel = {
                    ndviEnhancedPath: pathsMap['Core.Sentinel.NDVI.Enhanced.Path'] || '',
                    ndviPath: pathsMap['Core.Sentinel.NDVI.Path'] || '',
                    inspectionPath: pathsMap['Core.Sentinel.Inspection.Path'] || '',
                    ndmiSoilPngPath: pathsMap['Core.Sentinel.NDMI.Soil.PNG.Path'] || '',
                };

                this._appConfigurationObject.planet = {
                    inspectionPath: pathsMap['Core.Planet.Inspection.Path'] || '',
                    ndviPath: pathsMap['Core.Planet.NDVI.Path'] || '',
                };

                this.saveToSessionStorage();
                return true;
            }

            return false;
        } catch (error) {
            console.error('Satellite paths yükleme hatası:', error);
            return false;
        }
    }

    private performAppConfigurationsInitialization(keys: AppConfigKeys): void {
        const activeKeys = [
            keys.agreementSalesEnKey,
            keys.mixpanelProjectTokenKey,
            keys.mixpanelBaseUrlKey,
            keys.googleAnalyticsMeasurementIdKey,
            keys.cosmosDbExamplesUrlKey,
            keys.signalrNegotiateUrlKey,
        ];

        firstValueFrom(
            this._configService.configuration_GetConfiguration(activeKeys).pipe(
                catchError((error) => {
                    console.error('Error initializing language:', error);
                    return of(null);
                }),
                tap((response) => {
                    if (response && response.result) {
                        this.configureAppConfigurations(response.result as unknown as KeyValueModel[]);
                    } else {
                        this.setFallbackAppConfigurations();
                    }
                }),
                finalize(() => {
                    this._appConfigurationsSubject.next(this._appConfigurations);
                    this._appConfigurationsSubject.complete();
                })
            )
        );
    }

    private configureAppConfigurations(data: KeyValueModel[]): void {
        this._appConfigurations = data.reduce(
            (acc, item) => {
                acc[item.key] = item.value;
                return acc;
            },
            {} as Record<string, string>
        );

        this._appConfigurationObject = this.mapToConfigurationObject(data);

        this.saveToSessionStorage();
    }

    private mapToConfigurationObject(data: KeyValueModel[]): AppConfiguration {
        const configMap = data.reduce(
            (acc, item) => {
                acc[item.key] = item.value;
                return acc;
            },
            {} as Record<string, string>
        );

        return {
            agreements: this.parseAgreements(configMap[this._appConfigKeys.agreementSalesEnKey]),
            mixpanel: {
                projectToken: configMap[this._appConfigKeys.mixpanelProjectTokenKey] || '',
                baseUrl: configMap[this._appConfigKeys.mixpanelBaseUrlKey] || '',
            },
            googleAnalytics: {
                measurementId: configMap[this._appConfigKeys.googleAnalyticsMeasurementIdKey] || '',
            },
            sentinel: {
                ndviEnhancedPath: '',
                ndviPath: '',
                inspectionPath: '',
                ndmiSoilPngPath: '',
            },
            planet: {
                inspectionPath: '',
                ndviPath: '',
            },
        };
    }

    private parseAgreements(agreementsJson: string): AppConfiguration['agreements'] {
        try {
            if (!agreementsJson) {
                return {
                    tr: { accessKey: '', sales: '', terms: '' },
                    en: { accessKey: '', sales: '', terms: '' },
                };
            }
            return JSON.parse(agreementsJson);
        } catch (error) {
            console.error('Error parsing agreements JSON:', error);
            return {
                tr: { accessKey: '', sales: '', terms: '' },
                en: { accessKey: '', sales: '', terms: '' },
            };
        }
    }

    private setFallbackAppConfigurations(): void {
        this._appConfigurations = undefined;
        this._appConfigurationObject = undefined;
        // Clear from session storage
        this.clearSessionStorage();
    }

    private saveToSessionStorage(): void {
        if (this._appConfigurations) {
            this._sessionStorageService.setItem(this.SESSION_STORAGE_KEY, this._appConfigurations);
        }
        if (this._appConfigurationObject) {
            this._sessionStorageService.setItem(this.SESSION_STORAGE_OBJECT_KEY, this._appConfigurationObject);
        }
    }

    private loadFromSessionStorage(): Record<string, string> | null {
        const cached = this._sessionStorageService.getItem<Record<string, string>>(this.SESSION_STORAGE_KEY);
        if (cached) {
        }
        return cached;
    }

    private loadObjectFromSessionStorage(): AppConfiguration | null {
        const cached = this._sessionStorageService.getItem<AppConfiguration>(this.SESSION_STORAGE_OBJECT_KEY);
        if (cached) {
        }
        return cached;
    }

    private clearSessionStorage(): void {
        this._sessionStorageService.removeItem(this.SESSION_STORAGE_KEY);
        this._sessionStorageService.removeItem(this.SESSION_STORAGE_OBJECT_KEY);
    }

    clearConfigurations(): void {
        this._appConfigurations = undefined;
        this._appConfigurationObject = undefined;
        this._hasKeyValues = false;
        this.clearSessionStorage();
    }

    getConfigValue(key: string): string | undefined {
        return this._appConfigurations?.[key];
    }

    hasConfigKey(key: string): boolean {
        return this._appConfigurations?.[key] !== undefined;
    }

    get configuration(): AppConfiguration | undefined {
        return this._appConfigurationObject;
    }

    get mixpanelProjectToken(): string | undefined {
        return this._appConfigurationObject?.mixpanel.projectToken;
    }

    get mixpanelConfig(): { projectToken?: string; baseUrl?: string } {
        const mixpanel = this._appConfigurationObject?.mixpanel;
        return {
            projectToken: mixpanel?.projectToken,
            baseUrl: mixpanel?.baseUrl,
        };
    }

    get googleAnalyticsMeasurementId(): string | undefined {
        return this._appConfigurationObject?.googleAnalytics.measurementId;
    }

    get cosmosDbExamplesUrl(): string | undefined {
        return this.getConfigValue(this._appConfigKeys.cosmosDbExamplesUrlKey);
    }

    get appConfigurations(): Record<string, string> | undefined {
        return this._appConfigurations;
    }

    get sentinelPaths() {
        return this._appConfigurationObject?.sentinel;
    }

    get planetPaths() {
        return this._appConfigurationObject?.planet;
    }

    get signalrNegotiateUrl(): string | undefined {
        return this.getConfigValue(this._appConfigKeys.signalrNegotiateUrlKey);
    }
}
